import { Button, Switch } from '@ui';
import { Package, CreditCard, Clock, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@ui/lib/utils';
import type { PricingPlan, PricingPlanType } from '../types';

const TYPE_CONFIG: Record<PricingPlanType, {
    icon: typeof Package;
    label: string;
    bgColor: string;
    textColor: string;
    borderColor: string;
}> = {
    HOURLY: {
        icon: Clock,
        label: '시간제',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-600',
        borderColor: 'border-blue-200',
    },
    PACKAGE: {
        icon: Package,
        label: '횟수제',
        bgColor: 'bg-green-50',
        textColor: 'text-green-600',
        borderColor: 'border-green-200',
    },
    PREPAID: {
        icon: CreditCard,
        label: '선불',
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-600',
        borderColor: 'border-purple-200',
    },
};

interface PlanCardProps {
    plan: PricingPlan;
    onEdit: (plan: PricingPlan) => void;
    onToggle: (plan: PricingPlan) => void;
    onDelete: (plan: PricingPlan) => void;
    isToggling?: boolean;
}

export function PlanCard({ plan, onEdit, onToggle, onDelete, isToggling }: PlanCardProps) {
    const typeConfig = TYPE_CONFIG[plan.type];
    const Icon = typeConfig.icon;

    // 이용권 상세 정보 렌더링
    const renderDetails = () => {
        switch (plan.type) {
            case 'HOURLY':
                return (
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                        <span className="font-semibold">{plan.hourlyRate?.toLocaleString()}원</span> / 시간
                        {plan.minimumMinutes && (
                            <span className="text-neutral-500 dark:text-neutral-400"> (최소 {plan.minimumMinutes}분)</span>
                        )}
                    </p>
                );
            case 'PACKAGE':
                const perCount = plan.totalAmount && plan.totalCount
                    ? Math.round(plan.totalAmount / plan.totalCount)
                    : 0;
                return (
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                        <span className="font-semibold">{plan.totalAmount?.toLocaleString()}원</span>
                        {' / '}
                        <span className="font-semibold">{plan.totalCount}회</span>
                        {perCount > 0 && (
                            <span className="text-neutral-500 dark:text-neutral-400"> (회당 {perCount.toLocaleString()}원)</span>
                        )}
                    </p>
                );
            case 'PREPAID':
                return (
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                        충전 금액 <span className="font-semibold">{plan.chargeAmount?.toLocaleString()}원</span>
                    </p>
                );
            default:
                return null;
        }
    };

    const handleToggleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggle(plan);
    };

    return (
        <div
            className={cn(
                'rounded-lg border bg-white dark:bg-neutral-900 p-4 transition-all hover:shadow-md',
                !plan.active && 'opacity-60 bg-neutral-50 dark:bg-neutral-800/50'
            )}
        >
            {/* 헤더 */}
            <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                    {/* 타입 아이콘 */}
                    <div className={cn('p-2 rounded-lg', typeConfig.bgColor)}>
                        <Icon className={cn('h-5 w-5', typeConfig.textColor)} />
                    </div>
                    <div>
                        <h3 className="font-semibold text-neutral-800 dark:text-neutral-100">{plan.name}</h3>
                        <span className={cn(
                            'inline-flex items-center text-xs px-2 py-0.5 rounded-full',
                            typeConfig.bgColor,
                            typeConfig.textColor
                        )}>
                            {typeConfig.label}
                        </span>
                    </div>
                </div>

                {/* 활성 토글 */}
                <div className="flex items-center gap-2">
                    <span className={cn(
                        'text-xs font-medium',
                        plan.active ? 'text-green-600' : 'text-neutral-400 dark:text-neutral-500 dark:text-neutral-400'
                    )}>
                        {plan.active ? '활성' : '비활성'}
                    </span>
                    <Switch
                        checked={plan.active}
                        onCheckedChange={() => onToggle(plan)}
                        disabled={isToggling}
                    />
                </div>
            </div>

            {/* 상세 정보 */}
            <div className="mb-3">
                {renderDetails()}
            </div>

            {/* 설명 */}
            {plan.description && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">{plan.description}</p>
            )}

            {/* 액션 버튼 */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(plan)}
                    className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-800 dark:hover:text-neutral-100"
                >
                    <Pencil className="h-4 w-4 mr-1" />
                    수정
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(plan)}
                    className="text-danger-500 hover:text-danger-600 hover:bg-danger-50"
                >
                    <Trash2 className="h-4 w-4 mr-1" />
                    삭제
                </Button>
            </div>
        </div>
    );
}
